import { useCallback } from 'react';
import { filterNotEmpty } from '@utmost.co/utmost-design-system/src/utils/listHelpers';
import { useTypedQuery } from 'utils/hooks/useTypedQuery';
import { RsiData, RsiFieldKey, RsiRowOnErrorCallback, RsiRowValidator } from '../../../Types';
import {
  extractValueColumnsForReferenceIdValidation,
  UrnToValueMap,
  UrnToValuesMap,
  ValidationParams
} from '../../../validations/impl/validationUtils';
import { ValidateImporterRowDocument } from '../../../validations/data/__generated__/ValidateImporterRow.graphql';
import { ReferenceIdValidationsModel } from '../../../validations/useRowValidation';

interface ValidatorPayload {
  fieldsForExistenceDetection: RsiFieldKey[];
  fieldsForReferenceIdValidation: RsiFieldKey[];
  rowIsCreateMessage: string;
  rowIsUpdateMessage: string;
}

export function useValidateRowAgainstReferenceIdAndFieldExistence(
  {
    fieldsForExistenceDetection,
    fieldsForReferenceIdValidation,
    rowIsCreateMessage,
    rowIsUpdateMessage
  }: ValidatorPayload,
  validationParams: ValidationParams | undefined
): RsiRowValidator {
  const rowValidationApi = useServerRowValidationForUrnApi(validationParams);

  return useCallback(
    async (row: RsiData, addError: RsiRowOnErrorCallback, table: RsiData[]) => {
      const urnToValueForValidation = extractValueColumnsForReferenceIdValidation(row, [
        ...fieldsForExistenceDetection,
        ...fieldsForReferenceIdValidation
      ]);

      const serverValidationResults = await rowValidationApi(urnToValueForValidation);

      const existenceDetectionResults = serverValidationResults.filter((validation) =>
        fieldsForExistenceDetection.includes(validation.urn)
      );
      const validationErrors = serverValidationResults.filter(
        (validation) => !validation.result.valid && fieldsForReferenceIdValidation.includes(validation.urn)
      );

      existenceDetectionResults.map((validation) => {
        if (!validation.result?.valid) {
          addError(
            validation.urn,
            rowIsCreateMessage
              ? {
                  message: rowIsCreateMessage,
                  level: 'warning'
                }
              : {
                  message: validation.result.message,
                  level: 'error'
                }
          );
        } else if (validation.result?.valid) {
          addError(validation.urn, { message: rowIsUpdateMessage, level: 'info' });
        }
      });

      validationErrors.map((validation) => {
        if (!validation.result?.valid) {
          addError(validation.urn, { message: validation.result.message, level: 'error' });
        }
      });
    },
    [
      fieldsForExistenceDetection,
      fieldsForReferenceIdValidation,
      rowValidationApi,
      rowIsUpdateMessage,
      rowIsCreateMessage
    ]
  );
}

function useServerRowValidationForUrnApi(validationParams?: ValidationParams) {
  const { get: getValidateImporterRow } = useTypedQuery(ValidateImporterRowDocument);

  return useCallback(
    async ({
      urnToValueMap,
      urnToValuesMap
    }: {
      urnToValueMap: UrnToValueMap;
      urnToValuesMap: UrnToValuesMap;
    }): Promise<ReferenceIdValidationsModel> => {
      const urnsToValidate = Object.keys({ ...(urnToValueMap ?? {}), ...(urnToValuesMap ?? {}) });

      if (urnsToValidate.length === 0) return [];
      const referenceEntityExistsResp = await getValidateImporterRow({
        variables: {
          input: {
            currentEntityReferenceId: validationParams?.targetReferenceId,
            urnToValueMap,
            urnToValuesMap
          }
        }
      });

      return filterNotEmpty(
        referenceEntityExistsResp?.data?.validateReferenceIdsRow?.validations ?? []
      ) as ReferenceIdValidationsModel;
    },
    [validationParams, getValidateImporterRow]
  );
}
